// LuxurySessionTracker - Tracks high-value session signals and returning visitors
// Fires GA4 events: high_value_session, repeat_visitor_7d, repeat_visitor_30d
// Also records luxury intent scoring signals

import { useEffect, useRef } from 'react';
import { 
  trackHighValueSession, 
  trackRepeatVisitor7d, 
  trackRepeatVisitor30d 
} from '@/utils/analytics';
import { recordSignal } from '@/lib/luxuryIntentScoring';

const STORAGE_KEY = 'yv_visit_ts';
const SESSION_START_KEY = 'yv_session_start';

export function LuxurySessionTracker() {
  const firedRef = useRef({ highValue: false, repeat7d: false, repeat30d: false });

  useEffect(() => {
    // --- Returning visitor detection ---
    try {
      const now = Date.now();
      const stored = localStorage.getItem(STORAGE_KEY);
      
      if (stored) {
        const lastVisit = parseInt(stored, 10);
        const daysSince = (now - lastVisit) / (1000 * 60 * 60 * 24);
        
        if (daysSince <= 7 && !firedRef.current.repeat7d) {
          firedRef.current.repeat7d = true;
          trackRepeatVisitor7d();
          recordSignal('repeat_visit_7d');
        } else if (daysSince <= 30 && !firedRef.current.repeat30d) {
          firedRef.current.repeat30d = true;
          trackRepeatVisitor30d();
        }
      }
      
      localStorage.setItem(STORAGE_KEY, now.toString());
    } catch {
      // localStorage unavailable
    }

    // --- Session start tracking ---
    if (!sessionStorage.getItem(SESSION_START_KEY)) {
      sessionStorage.setItem(SESSION_START_KEY, Date.now().toString());
    }

    // --- High-value session detection (time >3min + scroll >75%) ---
    let scrollDepthMax = 0;

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight > 0) {
        const depth = Math.round((scrollTop / scrollHeight) * 100);
        if (depth > scrollDepthMax) scrollDepthMax = depth;
      }
    };

    const checkHighValue = () => {
      if (firedRef.current.highValue) return;
      
      const startStr = sessionStorage.getItem(SESSION_START_KEY);
      if (!startStr) return;
      
      const elapsed = (Date.now() - parseInt(startStr, 10)) / 1000;
      
      if (elapsed >= 180 && scrollDepthMax >= 75) {
        firedRef.current.highValue = true;
        trackHighValueSession({
          time_on_site_seconds: Math.round(elapsed),
          scroll_depth: scrollDepthMax,
        });
        recordSignal('time_on_site_3min');
        recordSignal('scroll_depth_75');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    const interval = setInterval(checkHighValue, 15000); // Check every 15s

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(interval);
    };
  }, []);

  return null; // Invisible tracking component
}
